import { assertDefined, isDefined, mapKeyBy, memoize } from '../../../lib/util';
import type { EventTimeline } from '../../core/Timelines';
import type {
  Currency,
  OrderId,
  VoyageCharterId,
  VoyageIncome_VoyageCharterFragment,
} from '../../../graphql';
import {
  PaymentUnit,
  useCreateVoyageCharterOrderFreightMutation,
  useUpdateVoyageCharterOrderFreightMutation,
} from '../../../graphql';
import type { ClientId } from '../../core/ClientId';
import { calculateFreightAmount } from '../../../lib/freightCalculation';
import { useCallback, useMemo, useState } from 'react';
import type { TimeData } from '../../core/TimeData';
import { isLoadEventTimeData } from '../../core/TimeData';
import {
  getConsumptionByPortCall,
  getPortCallIdsByOrder,
  getVoyageCharterBafIncomes,
} from './baf';
import type { BunkerConsumptionByPortCall } from '../costs/bunkerCost';
import { compact } from 'lodash';
import type { ScenarioOrder } from '../../../lib/planningData';

import type { GlobalStore, User } from '../../../state/zustand';
import {
  useCurrencyRates,
  useCurrentUser,
  useDefaultCurrency,
} from '../../../state/zustand';
import type { BunkerPriceByGradeName } from '../../hooks/useBunkerPrices';
import { useBunkerPrices } from '../../hooks/useBunkerPrices';
import { getVoyageCharterCommissionIncomes } from './commission';
import type { LoadedAmount } from '../loadedAmounts';
import { getTimelineLoadedAmounts } from '../loadedAmounts';
import { type DefaultMap, mapGroupBy } from '@seaber/common/lib/collections';
import type { PnlData } from '../../hooks/usePnlData';
import type { Distances } from '../../distances/context';
import {
  addMissingFreights,
  doNotAddMissingFreights,
  getFreightByLoadedAmount,
  getFreightIncome,
  getFreightsByOrderId,
  type OrderFreight,
} from './orderFreight';
import { getVoyageCharterExtraFreightIncome } from './extraFreight';
import type { WorldscaleFreightRateMap } from '../../../lib/worldscaleFreight';
import type {
  FreightRateOverride,
  ShipmentFreightOverridesByShipmentId,
} from '@seaber/common/core/scenario';
import type {
  ScenarioShipmentExtraFreight,
  ScenarioShipmentFreight,
} from '../../Scenario';

export type IncomeParam = {
  name: string;
  value: string | number | null;
  testId?: string;
};

export type Income = {
  type:
    | 'ORDER_FREIGHT'
    | 'VOYAGE_CHARTER_LUMPSUM'
    | 'EXTRA_FREIGHT'
    | 'BAF'
    | 'COMMISSION'
    | 'SHIPMENT_FREIGHT'
    | 'SHIPMENT_EXTRA_FREIGHT';
  name: string;
  value: number | null;
  orderId: OrderId | null;
  voyageCharterId: VoyageCharterId | null;
  params: IncomeParam[];
  shipmentClientId?: ClientId | null;
};

export type GetIncomeForLoadedAmount = (
  loadedAmounts: LoadedAmount[],
  voyageCharterIds: Set<VoyageCharterId>,
  portCallIdsByOrder: Map<OrderId, Set<ClientId>>,
  bunkerConsumptionByPortCall: BunkerConsumptionByPortCall,
  estimateIdOrScenario?: ClientId | 'scenario',
) => Income[];

const getOrderVcRelsByIncomingVcId = memoize(
  (orderById: Map<number, ScenarioOrder>) =>
    mapGroupBy(
      Array.from(
        orderById.values(),
        (o): ScenarioOrder['incomingVoyageCharterRelation'] =>
          o.incomingVoyageCharterRelation,
      ).filter(isDefined),
      (o) => o.voyageCharterId,
    ),
);

export function incomeForLoadedAmountGetter(
  orderById: Map<OrderId, ScenarioOrder>,
  freightByOrderId: DefaultMap<OrderId, OrderFreight[]>,
  voyageCharterById: Map<VoyageCharterId, VoyageIncome_VoyageCharterFragment>,
  currencyRates: GlobalStore['currencyRates'],
  defaultCurrency: Currency,
  bunkerPriceByGradeName: BunkerPriceByGradeName,
  worldscaleFreightRates: WorldscaleFreightRateMap,
  freightOverridesByEstimateIdOrScenario?: FreightOverridesByEstimateIdOrScenario,
  shipmentFreightByShipmentClientId?: Map<ClientId, ScenarioShipmentFreight>,
  shipmentExtraFreightByShipmentClientId?: Map<
    ClientId,
    ScenarioShipmentExtraFreight[]
  >,
  shipmentFreightOverridesByEstimateIdOrScenario?: ShipmentFreightOverridesByEstimateIdOrScenario,
): GetIncomeForLoadedAmount {
  const orderVcRelsByIncomingVcId = getOrderVcRelsByIncomingVcId(orderById);
  return (
    loadedAmounts,
    incomingVoyageCharterIds,
    portCallIdsByOrder,
    bunkerConsumptionByPortCall,
    estimateIdOrScenario: ClientId | 'scenario' = 'scenario',
  ) => {
    const incomes: Income[] = [];
    const freightIncomeByOrderId: Map<number, number> = new Map();

    const shipmentsHaveFreight = new Set<ClientId>();
    // Get shipment freight overrides for current estimate or scenario
    const shipmentFreightOverrides =
      shipmentFreightOverridesByEstimateIdOrScenario?.[estimateIdOrScenario];

    if (shipmentFreightByShipmentClientId) {
      shipmentFreightByShipmentClientId.forEach((freight, shipmentClientId) => {
        if (freight.freightAmount) {
          shipmentsHaveFreight.add(shipmentClientId);
        }
      });
    }
    // Also mark shipments with overrides as having freight
    if (shipmentFreightOverrides) {
      shipmentFreightOverrides.forEach((override, shipmentClientId) => {
        if (override.freight !== null) {
          shipmentsHaveFreight.add(shipmentClientId);
        }
      });
    }

    /* Get freight for each loaded order */
    loadedAmounts.forEach((loadedAmount) => {
      const { orderId } = loadedAmount;
      const order = orderById.get(orderId);
      if (!order) return;

      // Skip order freight calculation if shipment freight exists
      if (order.shipmentId && shipmentsHaveFreight.has(order.shipmentId)) {
        return;
      }

      const freight = getFreightByLoadedAmount(freightByOrderId, loadedAmount);
      const override =
        freightOverridesByEstimateIdOrScenario?.[estimateIdOrScenario]?.[
          orderId
        ];
      if (!freight && !override) return;

      const overrideFreight = override
        ? {
            orderId: orderId,
            unit: override.freightUnit,
            price: override.freight,
            currency: freight?.currency ?? defaultCurrency,
            maxAmount: null,
            minAmount: null,
            lumpSumUnit: null,
            coaAssortmentMatchedBy: null,
          }
        : null;

      const { incomeAmount, params } = getFreightIncome(
        loadedAmount,
        overrideFreight || freight!,
        currencyRates,
        worldscaleFreightRates,
        order,
      );
      if (incomeAmount === null) return;

      if (overrideFreight && freight) {
        params.unshift({
          name: 'Original freight',
          value: `${freight.price} ${freight.currency}/${freight.unit}`,
        });
      }

      freightIncomeByOrderId.set(orderId, incomeAmount);

      if (
        incomeAmount === 0 &&
        order.incomingVoyageCharterRelation?.voyageCharterId &&
        voyageCharterById.get(
          order.incomingVoyageCharterRelation?.voyageCharterId,
        )?.lumpsum
      ) {
        // There is a lumpsum income so no need to show zero income from freights
        return;
      }

      incomes.push({
        type: 'ORDER_FREIGHT',
        name: `Order #${order.id} ${
          order.assortmentByAssortmentId?.name
        } ${order.locationByLoadPortId?.name} - ${
          order.locationByDischargePortId?.name
        }`,
        orderId,
        voyageCharterId:
          order.incomingVoyageCharterRelation?.voyageCharterId ?? null,
        value: incomeAmount,
        params,
      });
    });

    /* Get extra freights for each voyage charter */
    incomingVoyageCharterIds.forEach((vcId) => {
      const voyageCharter = voyageCharterById.get(vcId);
      if (!voyageCharter) return;

      if (voyageCharter.lumpsum) {
        const voyageCharterOrders =
          orderVcRelsByIncomingVcId
            .get(vcId)
            ?.map((node) =>
              node?.orderId ? orderById.get(node.orderId) : null,
            )
            .filter(isDefined) ?? [];

        incomes.push({
          type: 'VOYAGE_CHARTER_LUMPSUM',
          name: 'Total contract freight',
          orderId: null,
          voyageCharterId: voyageCharter.id,
          value: Number(voyageCharter.lumpsum),
          params: voyageCharterOrders
            .map(
              (order) =>
                `#${order?.id ?? 'N/A'} ${
                  order?.assortmentByAssortmentId?.name
                } ${order?.locationByLoadPortId?.name} - ${
                  order?.locationByDischargePortId?.name
                }`,
            )
            .map((orderString) => ({
              name: 'Order',
              value: orderString,
            })),
        });
      }

      const extraFreightIncomes = getVoyageCharterExtraFreightIncome(
        voyageCharter,
        loadedAmounts,
        freightIncomeByOrderId,
        orderVcRelsByIncomingVcId,
      );

      const bafIncomes = getVoyageCharterBafIncomes(
        voyageCharter,
        loadedAmounts,
        orderById,
        freightIncomeByOrderId,
        portCallIdsByOrder,
        bunkerConsumptionByPortCall,
        defaultCurrency,
        bunkerPriceByGradeName,
      );

      incomes.push(...extraFreightIncomes, ...bafIncomes);

      /* Get all incomes related to this voyage charter for commission calculation */
      const voyageCharterOrderIds = new Set<number>();
      for (const vcRel of orderVcRelsByIncomingVcId.get(vcId) ?? []) {
        voyageCharterOrderIds.add(vcRel.orderId);
      }

      const voyageCharterIncomes: Income[] = [
        ...bafIncomes,
        ...extraFreightIncomes,
        ...incomes.filter(
          (income) =>
            (income.type === 'ORDER_FREIGHT' ||
              income.type === 'VOYAGE_CHARTER_LUMPSUM') &&
            income.orderId &&
            voyageCharterOrderIds.has(income.orderId),
        ),
      ];

      const commissionIncomes = getVoyageCharterCommissionIncomes(
        voyageCharter,
        voyageCharterIncomes,
        loadedAmounts,
      );

      incomes.push(...commissionIncomes);
    });

    /* Calculate shipment freight for shipment */
    if (
      shipmentFreightByShipmentClientId?.size ||
      shipmentFreightOverrides?.size
    ) {
      const loadedAmountsByShipment = aggregateLoadedAmountsByShipment(
        loadedAmounts,
        orderById,
      );

      // Process all shipments that have freight (either from scenario or overrides)
      const processedShipments = new Set<ClientId>();

      const processShipmentFreight = (
        shipmentClientId: ClientId,
        freightAmount: number,
        freightUnit: PaymentUnit,
        isOverride: boolean,
      ) => {
        if (processedShipments.has(shipmentClientId)) return;
        processedShipments.add(shipmentClientId);

        const loadedData = loadedAmountsByShipment.get(shipmentClientId);
        if (!loadedData) return;

        const freightRate = Number(freightAmount);
        if (!isDefined(freightRate)) {
          console.error('Currency conversion failed for shipment freight', {
            shipmentClientId,
            freightAmount,
          });
          return;
        }

        const result = calculateShipmentFreightIncome(
          'SHIPMENT_FREIGHT',
          loadedData,
          freightRate,
          freightAmount,
          freightUnit,
          worldscaleFreightRates,
        );
        if (!result) return;

        const { incomeAmount, params } = result;
        const shipmentName = loadedData.orders[0]?.shipment?.name ?? '';

        if (isOverride) {
          const originalFreight =
            shipmentFreightByShipmentClientId?.get(shipmentClientId);
          if (originalFreight?.freightAmount) {
            params.unshift({
              name: 'Original freight',
              value: `${originalFreight.freightAmount} ${originalFreight.freightUnit}`,
            });
          }
        }

        params.push({
          name: 'Orders in shipment',
          value: loadedData.orders.map((o) => `#${o.id}`).join(', '),
        });

        loadedData.orders.forEach((order) => {
          freightIncomeByOrderId.set(order.id, incomeAmount);
        });

        incomes.push({
          type: 'SHIPMENT_FREIGHT',
          name: `Shipment Freight: ${shipmentName}`,
          orderId: null,
          voyageCharterId: null,
          shipmentClientId,
          value: incomeAmount,
          params,
        });
      };

      // First process overrides (they take priority)
      shipmentFreightOverrides?.forEach((override, shipmentClientId) => {
        if (override.freight !== null) {
          processShipmentFreight(
            shipmentClientId,
            override.freight,
            override.freightUnit,
            true,
          );
        }
      });

      // Then process scenario shipment freights (for shipments not already processed via override)
      shipmentFreightByShipmentClientId?.forEach(
        (shipmentFreight, shipmentClientId) => {
          if (shipmentFreight.freightAmount) {
            processShipmentFreight(
              shipmentClientId,
              Number(shipmentFreight.freightAmount),
              shipmentFreight.freightUnit,
              false,
            );
          }
        },
      );
    }

    /* Calculate shipment extra freight for each shipment */
    if (shipmentExtraFreightByShipmentClientId?.size) {
      const loadedAmountsByShipment = aggregateLoadedAmountsByShipment(
        loadedAmounts,
        orderById,
      );

      shipmentExtraFreightByShipmentClientId.forEach(
        (extraFreights, shipmentClientId) => {
          const loadedData = loadedAmountsByShipment.get(shipmentClientId);
          if (!loadedData) return;

          const shipmentName = loadedData.orders[0]?.shipment?.name ?? '';

          extraFreights.forEach((extraFreight) => {
            const freightRate = Number(extraFreight.freightAmount);

            if (!isDefined(freightRate)) {
              console.error(
                'Currency conversion failed for shipment extra freight',
                extraFreight,
              );
              return;
            }

            const result = calculateShipmentFreightIncome(
              'SHIPMENT_EXTRA_FREIGHT',
              loadedData,
              freightRate,
              extraFreight.freightAmount,
              extraFreight.freightUnit,
              worldscaleFreightRates,
            );
            if (!result) return;

            const { incomeAmount, params } = result;

            if (extraFreight.description) {
              params.push({
                name: 'Description',
                value: extraFreight.description,
              });
            }

            incomes.push({
              type: 'SHIPMENT_EXTRA_FREIGHT',
              name:
                extraFreight.name ?? `Shipment Extra Freight: ${shipmentName}`,
              orderId: null,
              voyageCharterId: null,
              shipmentClientId,
              value: incomeAmount,
              params,
            });
          });
        },
      );
    }

    return incomes;
  };
}

/**
 * Build the freight income getter from a scenario's voyage-charter and shipment-freight maps. Shared by
 * the committed derive (planningData) and suggestion generation (getSuggestions) so both rank loaded
 * cargo by identical freight income. Estimate-context overrides (args 8 and 11 of
 * incomeForLoadedAmountGetter) are unavailable to either caller and stay undefined.
 */
export function buildVoyageFreightIncomeGetter(
  orderById: Map<OrderId, ScenarioOrder>,
  incomingVoyageChartersById: Map<
    VoyageCharterId,
    VoyageIncome_VoyageCharterFragment
  >,
  currencyRates: GlobalStore['currencyRates'],
  defaultCurrency: Currency,
  bunkerPriceByGradeName: BunkerPriceByGradeName,
  worldscaleFreightRates: WorldscaleFreightRateMap,
  shipmentFreightByShipmentClientId:
    | Map<ClientId, ScenarioShipmentFreight>
    | undefined,
  shipmentExtraFreightByShipmentClientId:
    | Map<ClientId, ScenarioShipmentExtraFreight[]>
    | undefined,
): GetIncomeForLoadedAmount {
  const incomingFreightByOrderId = getFreightsByOrderId(
    incomingVoyageChartersById,
    orderById,
    doNotAddMissingFreights,
  );
  return incomeForLoadedAmountGetter(
    orderById,
    incomingFreightByOrderId,
    incomingVoyageChartersById,
    currencyRates,
    defaultCurrency,
    bunkerPriceByGradeName,
    worldscaleFreightRates,
    undefined,
    shipmentFreightByShipmentClientId,
    shipmentExtraFreightByShipmentClientId,
    undefined,
  );
}

export const useVoyageIncome = (
  incomingVoyageChartersById: PnlData['incomingVoyageChartersById'],
  worldscaleFreightRates: PnlData['worldscaleFreightRates'],
) => {
  const currentUser = useCurrentUser();
  const currencyRates = useCurrencyRates();
  const defaultCurrency = useDefaultCurrency();
  const { bunkerPriceByGradeName } = useBunkerPrices();

  const getTcTimelineIncome = useCallback(
    async (
      timeline: EventTimeline,
      focusedOrderIds?: Set<number>,
      freightOverrides?: FreightOverrideByOrder,
      shipmentFreightByShipmentClientId?: Map<
        ClientId,
        ScenarioShipmentFreight
      >,
      shipmentExtraFreightByShipmentClientId?: Map<
        ClientId,
        ScenarioShipmentExtraFreight[]
      >,
      estimateShipmentFreightOverrides?: ShipmentFreightOverridesByEstimateIdOrScenario,
    ): Promise<Income[]> => {
      assertDefined(currentUser, 'logged in user');

      const tds = timeline.voyageGroups.at(0)?.events ?? [];
      const allTimelineOrders = timeline.voyageGroups.at(0)?.orders ?? [];
      const focusedOrders = focusedOrderIds
        ? allTimelineOrders.filter((o) => focusedOrderIds.has(o.id))
        : allTimelineOrders;
      const orderById = mapKeyBy(focusedOrders, (o) => o.id);
      const timelineVoyageCharterIds = new Set(
        compact(
          focusedOrders.map(
            (o) => o.incomingVoyageCharterRelation?.voyageCharterId,
          ),
        ),
      );

      const freightByOrderId = getFreightsByOrderId(
        incomingVoyageChartersById,
        orderById,
        freightOverrides ? addMissingFreights : doNotAddMissingFreights,
      );

      const loadedAmounts = getTimelineLoadedAmounts(timeline, focusedOrderIds);

      const freightOverridesByEstimateIdOrScenario = {
        [timeline.estimateIdOrScenario]: freightOverrides,
      };

      const getIncome = incomeForLoadedAmountGetter(
        orderById,
        freightByOrderId,
        incomingVoyageChartersById,
        currencyRates,
        defaultCurrency,
        bunkerPriceByGradeName,
        worldscaleFreightRates,
        freightOverridesByEstimateIdOrScenario,
        shipmentFreightByShipmentClientId,
        shipmentExtraFreightByShipmentClientId,
        estimateShipmentFreightOverrides,
      );

      const portCallIdsByOrder = getPortCallIdsByOrder(tds);
      const bunkerConsumptionByPortCall = getConsumptionByPortCall(tds);

      return getIncome(
        loadedAmounts,
        timelineVoyageCharterIds,
        portCallIdsByOrder,
        bunkerConsumptionByPortCall,
        timeline.estimateIdOrScenario,
      );
    },
    [
      bunkerPriceByGradeName,
      currencyRates,
      currentUser,
      defaultCurrency,
      incomingVoyageChartersById,
      worldscaleFreightRates,
    ],
  );

  return { getTcTimelineIncome };
};

export type FreightOverrideByOrder = Record<number, FreightRateOverride>;

export type FreightOverridesByEstimateIdOrScenario = Partial<
  Record<ClientId | 'scenario', FreightOverrideByOrder | null>
>;

export type ShipmentFreightOverridesByEstimateIdOrScenario = Partial<
  Record<ClientId | 'scenario', ShipmentFreightOverridesByShipmentId | null>
>;

export function useFreightOverrides(orderById: Map<number, ScenarioOrder>) {
  const [createOrderFreight] = useCreateVoyageCharterOrderFreightMutation();
  const [updateOrderFreight] = useUpdateVoyageCharterOrderFreightMutation();

  const [freightOverrides, setFreightOverrides] =
    useState<FreightOverrideByOrder>({});

  const toggleFreightOverride = useCallback(
    (
      orderId: number,
      existingValue: number | null,
      existingUnit?: PaymentUnit,
    ) => {
      setFreightOverrides((overrides) => {
        const isEnabled = !!overrides[orderId];
        if (isEnabled) {
          const { [orderId]: _omitted, ...nextOverrides } = overrides;
          return nextOverrides;
        } else {
          return {
            ...overrides,
            [orderId]: {
              freight: existingValue ?? 0,
              freightUnit: existingUnit ?? PaymentUnit.MT,
            },
          };
        }
      });
    },
    [],
  );

  const setFreightOverride = useCallback(
    (orderId: number, freightOverride: FreightRateOverride) => {
      setFreightOverrides((overrides) => {
        return {
          ...overrides,
          [orderId]: freightOverride,
        };
      });
    },
    [],
  );

  // Remove the local override if present. Distinct from `toggleFreightOverride`,
  // which would add a zero-freight entry when no override exists yet.
  const removeFreightOverride = useCallback((orderId: number) => {
    setFreightOverrides((overrides) => {
      if (!(orderId in overrides)) return overrides;
      const { [orderId]: _omitted, ...rest } = overrides;
      return rest;
    });
  }, []);

  const persistOverride = useCallback(
    async (orderId: number) => {
      const override = freightOverrides[orderId];
      if (!override) {
        console.error('No override found for order', orderId);
        return;
      }

      const order = orderById.get(orderId);
      const vcRel = order?.incomingVoyageCharterRelation;

      if (!vcRel) {
        console.error('No voyage charter relation found for order', orderId);
        return;
      }

      const persistedFreight =
        vcRel.voyageCharterOrderFreightsByOrderVoyageCharterRelationId.nodes.at(
          0,
        );

      if (persistedFreight) {
        await updateOrderFreight({
          variables: {
            input: {
              id: persistedFreight.id,
              voyageCharterOrderFreightPatch: {
                freightAmount: override.freight,
                freightUnit: override.freightUnit,
              },
            },
          },
        });
      } else {
        await createOrderFreight({
          variables: {
            input: {
              voyageCharterOrderFreight: {
                freightAmount: override.freight,
                freightUnit: override.freightUnit,
                orderVoyageCharterRelationId: vcRel.id,
              },
            },
          },
        });
      }

      setFreightOverrides((currentOverrides) => {
        const { [orderId]: _omitted, ...nextOverrides } = currentOverrides;
        return nextOverrides;
      });
    },
    [freightOverrides, orderById, updateOrderFreight, createOrderFreight],
  );

  return useMemo(
    () => ({
      freightOverrides,
      persistOverride,
      setFreightOverride,
      removeFreightOverride,
      toggleFreightOverride,
    }),
    [
      freightOverrides,
      persistOverride,
      setFreightOverride,
      removeFreightOverride,
      toggleFreightOverride,
    ],
  );
}

export const isEuEtsCostEnabledForUser = (user: User | null) =>
  !!user?.rootOrganization?.euEtsCosts;

export type LoadedByShipment = {
  mass: number;
  volume: number;
  pcs: number;
  orders: ScenarioOrder[];
};

function calculateShipmentFreightIncome(
  type: 'SHIPMENT_FREIGHT' | 'SHIPMENT_EXTRA_FREIGHT',
  loadedData: LoadedByShipment,
  freightRate: number,
  freightAmount: string | number,
  freightUnit: PaymentUnit,
  worldscaleFreightRates: WorldscaleFreightRateMap,
): {
  incomeAmount: number;
  params: IncomeParam[];
} | null {
  const incomeAmount = calculateFreightAmount({
    mass: loadedData.mass,
    volume: loadedData.volume,
    pcs: loadedData.pcs,
    freightRate,
    freightUnit,
    worldscaleFreightRates,
    order: undefined,
  });

  if (incomeAmount === null) return null;

  const isExtraFreight = type === 'SHIPMENT_EXTRA_FREIGHT';
  const loadedTestIdPrefix = isExtraFreight
    ? 'shipment-extra-freight'
    : 'shipment-loaded';
  const rateUnitTestIdPrefix = isExtraFreight
    ? 'shipment-extra-freight'
    : 'shipment-freight';

  const params: IncomeParam[] = [
    {
      name: 'Freight rate',
      value: freightAmount,
      testId: `${rateUnitTestIdPrefix}-rate`,
    },
    {
      name: 'Freight unit',
      value: freightUnit,
      testId: `${rateUnitTestIdPrefix}-unit`,
    },
  ];

  switch (freightUnit) {
    case PaymentUnit.PER_SET:
    case PaymentUnit.PER_PCS:
      params.push({
        testId: `${loadedTestIdPrefix}-pcs`,
        name: 'Total loaded (pcs)',
        value: loadedData.pcs,
      });
      break;
    case PaymentUnit.M3:
    case PaymentUnit.SCBM_UB:
    case PaymentUnit.FT3:
      params.push({
        testId: `${loadedTestIdPrefix}-volume`,
        name: 'Total loaded (cbm)',
        value: loadedData.volume,
      });
      break;
    case PaymentUnit.MT:
    case PaymentUnit.BDMT:
      params.push({
        testId: `${loadedTestIdPrefix}-mass`,
        name: 'Total loaded (mt)',
        value: loadedData.mass,
      });
      break;
    case PaymentUnit.FRT:
      params.push({
        testId: `${loadedTestIdPrefix}-mass`,
        name: 'Total loaded (mt)',
        value: loadedData.mass,
      });
      params.push({
        testId: `${loadedTestIdPrefix}-volume`,
        name: 'Total loaded (cbm)',
        value: loadedData.volume,
      });
      break;
    case PaymentUnit.LUMPSUM:
      if (isExtraFreight) {
        params.push({
          testId: `${loadedTestIdPrefix}-lumpsum`,
          name: 'Type',
          value: 'Lump sum',
        });
      }
      break;
  }

  return { incomeAmount, params };
}

export function aggregateLoadedAmountsByShipment(
  loadedAmounts: LoadedAmount[],
  orderById: Map<number, ScenarioOrder>,
): Map<ClientId, LoadedByShipment> {
  const result = new Map<ClientId, LoadedByShipment>();

  loadedAmounts.forEach((loadedAmount) => {
    const order = orderById.get(loadedAmount.orderId);
    if (!order?.shipmentId) return;

    const existing = result.get(order.shipmentId) ?? {
      mass: 0,
      volume: 0,
      pcs: 0,
      orders: [],
    };

    existing.mass += loadedAmount.loadedMass ?? 0;
    existing.volume += loadedAmount.loadedVolume ?? 0;
    existing.pcs += loadedAmount.loadedPcs ?? 0;
    if (!existing.orders.find((o) => o.id === order.id)) {
      existing.orders.push(order);
    }

    result.set(order.shipmentId, existing);
  });

  return result;
}

export function getTDIncomeAndMassDistance(
  timeData: TimeData[],
  getIncomeForLoadedAmount: GetIncomeForLoadedAmount | undefined,
  distances: Distances,
  opts?: { firstIndex: number; lastIndex: number },
  estimateIdOrScenario: ClientId | 'scenario' = 'scenario',
) {
  let totalIncome = 0;
  let totalMassDistance = 0;
  let totalCost = 0;
  let totalTcCost = 0;
  let totalPortCostEstimate = 0;
  let totalCommissionIncome = 0;
  let totalFreightIncome = 0;
  const loadedVoyageCharterIds = new Set<VoyageCharterId>();
  const loadedAmounts: LoadedAmount[] = [];
  const missingIncomeOrderIds: OrderId[] = [];

  for (
    let i = opts?.firstIndex ?? 0;
    i <= (opts?.lastIndex ?? timeData.length - 1);
    ++i
  ) {
    const td = timeData[i];
    if (isLoadEventTimeData(td)) {
      const {
        order: {
          id,
          dischargePortId,
          loadPortId,
          incomingVoyageCharterRelation,
        },
      } = td.event;
      if (!loadPortId) {
        continue;
      }

      loadedAmounts.push({
        orderId: id,
        loadedMass: td.mass,
        loadedVolume: td.volume,
        loadedPcs: td.pcs,
        cargoAmount: Number(td.event.order.cargoAmount),
        cargoUnit: td.event.order.cargoUnitByCargoUnitId?.name || null,
      });

      const voyageCharterId =
        incomingVoyageCharterRelation?.voyageCharterId ?? null;

      if (voyageCharterId !== null) {
        loadedVoyageCharterIds.add(voyageCharterId);
      }

      const distance = distances.get(loadPortId, dischargePortId, false)?.dist;
      if (!distance) {
        continue;
      }
      const massDistance = td.mass * distance;
      totalMassDistance += massDistance;
    }
    totalCost += td.costs.totalCost;
    totalTcCost += td.costs.tcCost;
    totalPortCostEstimate += td.costs.portCostEstimate ?? 0;
  }

  const portCallIdsByOrder = getPortCallIdsByOrder(timeData);
  const bunkerConsumptionByPortCall = getConsumptionByPortCall(timeData);

  if (getIncomeForLoadedAmount) {
    const incomes = getIncomeForLoadedAmount(
      loadedAmounts,
      loadedVoyageCharterIds,
      portCallIdsByOrder,
      bunkerConsumptionByPortCall,
      estimateIdOrScenario,
    );

    for (const income of incomes) {
      const { orderId, value } = income;
      if (!value && orderId !== null) {
        missingIncomeOrderIds.push(orderId);
      }

      switch (income.type) {
        case 'COMMISSION': {
          totalCommissionIncome += value ?? 0;
          break;
        }
        case 'ORDER_FREIGHT': {
          totalFreightIncome += value ?? 0;
          break;
        }
        case 'VOYAGE_CHARTER_LUMPSUM':
        case 'EXTRA_FREIGHT':
        case 'BAF': {
          break;
        }
      }

      totalIncome += value ?? 0;
    }
  }

  return {
    income: totalIncome,
    massDistance: totalMassDistance,
    missingIncomeOrderIds,
    totalCost,
    tcCost: totalTcCost,
    portCostEstimate: totalPortCostEstimate,
    loadedAmounts,
    loadedVoyageCharterIds,
    totalCommissionIncome,
    totalFreightIncome,
  };
}
